import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { validateStep, validateAll, launchPlan } from "../../services/strategyService";

// Composant intelligent — affiché après « générer plan média ». Il résume les
// étapes stratégiques, laisse l'utilisateur en valider, puis lance en batch les
// campagnes VALIDÉES (brouillon forcé côté serveur : aucune diffusion).
const TEMP = { cold: "bg-sky-100 text-sky-700", warm: "bg-amber-100 text-amber-700", hot: "bg-red-100 text-red-700" };
const PHASE_EMOJI = { test: "🧪", lookalike: "👥", scaling: "📈", retargeting: "🔁" };
const STATUS_BADGE = {
  draft: "bg-gray-100 text-gray-500", validated: "bg-green-100 text-green-700",
  launching: "bg-amber-100 text-amber-700", launched: "bg-indigo-100 text-indigo-700",
  failed: "bg-red-100 text-red-700", skipped: "bg-gray-100 text-gray-400",
};
const STATUS_LABEL = { draft: "à valider", validated: "validée", launching: "lancement…", launched: "lancée ✓", failed: "échec", skipped: "ignorée" };

const StrategySteps = ({ planId, steps: initial, summary: initialSummary, onChange }) => {
  const [steps, setSteps] = useState(initial || []);
  const [busy, setBusy] = useState(false);
  const [launching, setLaunching] = useState(false);

  // Résumé recalculé localement pour un affichage instantané.
  const summary = useMemo(() => {
    const s = { total: steps.length, validated: 0, launched: 0, failed: 0, daily: 0, byPhase: {}, byTemp: {} };
    for (const st of steps) {
      if (st.status === "validated") s.validated += 1;
      if (st.status === "launched") s.launched += 1;
      if (st.status === "failed") s.failed += 1;
      if (["draft", "validated"].includes(st.status)) s.daily += Number(st.daily_budget) || 0;
      s.byPhase[st.phase] = (s.byPhase[st.phase] || 0) + 1;
      const t = st.audience?.temperature || "n/a";
      s.byTemp[t] = (s.byTemp[t] || 0) + 1;
    }
    s.daily = Math.round(s.daily * 100) / 100;
    s.monthly = Math.round(s.daily * 30);
    return s;
  }, [steps]);

  const patchLocal = (id, patch) => setSteps((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));

  const toggle = async (step) => {
    const next = step.status !== "validated";
    patchLocal(step.id, { status: next ? "validated" : "draft" });
    try { await validateStep(step.id, next); } catch { toast.error("Action impossible."); patchLocal(step.id, { status: step.status }); }
  };

  const toggleAll = async (validated) => {
    setBusy(true);
    setSteps((prev) => prev.map((s) => (["draft", "validated"].includes(s.status) ? { ...s, status: validated ? "validated" : "draft" } : s)));
    try { await validateAll(planId, validated); } catch { toast.error("Action impossible."); }
    finally { setBusy(false); }
  };

  const launch = async () => {
    if (summary.validated === 0) return toast.warn("Validez au moins une étape.");
    if (!window.confirm(`Lancer ${summary.validated} étape(s) en brouillon ? Aucune diffusion ne démarre — les campagnes sont créées en pause.`)) return;
    setLaunching(true);
    setSteps((prev) => prev.map((s) => (s.status === "validated" ? { ...s, status: "launching" } : s)));
    try {
      const res = await launchPlan(planId);
      setSteps(res.steps || []);
      onChange && onChange(res);
      toast.success(`${res.launched} campagne(s) créée(s) en brouillon${res.failed ? ` · ${res.failed} échec(s)` : ""}.`);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Lancement impossible.");
      setSteps(initial || []);
    } finally { setLaunching(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
      {/* Bandeau résumé intelligent */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <p className="font-bold text-[#221C51]">Plan média — {summary.total} étape{summary.total > 1 ? "s" : ""}</p>
          <p className="text-sm text-gray-500 mt-0.5">
            <b className="text-green-600">{summary.validated}</b> validée{summary.validated > 1 ? "s" : ""}
            {summary.launched > 0 && <> · <b className="text-indigo-600">{summary.launched}</b> lancée{summary.launched > 1 ? "s" : ""}</>}
            {summary.failed > 0 && <> · <b className="text-red-600">{summary.failed}</b> en échec</>}
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs text-gray-400">Budget si tout est lancé</p>
          <p className="font-bold text-[#221C51]">{summary.daily} €/j <span className="text-gray-400 font-normal">· ~{summary.monthly} €/mois</span></p>
        </div>
      </div>

      {/* Répartitions */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {Object.entries(summary.byPhase).map(([ph, n]) => (
          <span key={ph} className="text-[11px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-semibold">{PHASE_EMOJI[ph] || "•"} {ph} ×{n}</span>
        ))}
        {Object.entries(summary.byTemp).filter(([t]) => t !== "n/a").map(([t, n]) => (
          <span key={t} className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${TEMP[t] || "bg-gray-100 text-gray-600"}`}>{t} ×{n}</span>
        ))}
      </div>

      {/* Actions groupées */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <button onClick={() => toggleAll(true)} disabled={busy} className="text-sm font-semibold text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-1.5 hover:bg-green-100 disabled:opacity-50">Tout valider</button>
        <button onClick={() => toggleAll(false)} disabled={busy} className="text-sm font-medium text-gray-500 border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50 disabled:opacity-50">Tout dévalider</button>
        <button onClick={launch} disabled={launching || summary.validated === 0}
          className="ml-auto text-sm font-bold text-white bg-[#221C51] rounded-lg px-5 py-1.5 hover:bg-[#2e265e] disabled:opacity-50">
          {launching ? "Lancement…" : `Lancer ${summary.validated} étape(s) validée(s)`}
        </button>
      </div>

      {/* Tableau des étapes (défilement contenu — plus de bug de boutons) */}
      <div className="border border-gray-100 rounded-xl overflow-hidden">
        <div className="max-h-[420px] overflow-y-auto divide-y divide-gray-100">
          {steps.map((s) => {
            const on = s.status === "validated";
            const locked = ["launching", "launched"].includes(s.status);
            return (
              <div key={s.id} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50/60">
                {/* Case de validation */}
                <button onClick={() => !locked && toggle(s)} disabled={locked}
                  className={`shrink-0 w-5 h-5 rounded-md border flex items-center justify-center text-xs transition ${
                    on ? "bg-green-500 border-green-500 text-white" : "border-gray-300 text-transparent hover:border-green-400"} ${locked ? "opacity-50" : ""}`}>
                  ✓
                </button>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-[#221C51] truncate">{PHASE_EMOJI[s.phase] || "•"} {s.name}</p>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {s.audience?.temperature && <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${TEMP[s.audience.temperature] || "bg-gray-100"}`}>{s.audience.temperature}</span>}
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 font-semibold">{s.daily_budget ? `${s.daily_budget} €/j` : "CBO"}</span>
                    {s.creatives_count != null && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 font-semibold">{s.creatives_count} créas</span>}
                    {s.kill_rule && <span className="text-[10px] text-red-500 truncate max-w-[220px]">⚠ {s.kill_rule}</span>}
                  </div>
                  {s.status === "failed" && s.launch_error && <p className="text-[11px] text-red-500 mt-1 truncate">✗ {s.launch_error}</p>}
                  {s.status === "launched" && s.external_id && <p className="text-[11px] text-indigo-500 mt-1">id {s.external_id}</p>}
                </div>
                <span className={`shrink-0 text-[10px] px-2 py-0.5 rounded-full font-bold ${STATUS_BADGE[s.status] || "bg-gray-100"}`}>{STATUS_LABEL[s.status] || s.status}</span>
              </div>
            );
          })}
        </div>
      </div>

      <p className="text-[11px] text-gray-400 mt-3">
        🔒 Sécurité : les campagnes sont créées en <b>brouillon (pause)</b>. Aucune diffusion ni dépense tant que vous ne les activez pas dans le gestionnaire de la régie.
      </p>
    </div>
  );
};

export default StrategySteps;
